import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getEngineers } from '../../../services/masterDataService';

const TIME_UNITS = [
  { value: 'hours', label: 'Hours', toHours: 1 },
  { value: 'days', label: 'Days', toHours: 9 },
  { value: 'months', label: 'Months', toHours: 240 }
];

const ROLE_LABELS = { senior: 'Senior', mid: 'Mid-Level', junior: 'Junior', lead: 'Lead', architect: 'Architect', manager: 'Manager' };

const EngineerSelector = ({ selectedEngineers, onEngineersChange, currency }) => {
  const [masterEngineers, setMasterEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize local state from persisted selectedEngineers so restored data stays live
  const [durations, setDurations] = useState(() => {
    const init = {};
    selectedEngineers?.forEach(e => { if (e?.masterId) init[e.masterId] = e?.duration ?? 1; });
    return init;
  });
  const [timeUnits, setTimeUnits] = useState(() => {
    const init = {};
    selectedEngineers?.forEach(e => { if (e?.masterId) init[e.masterId] = e?.timeUnit ?? 'hours'; });
    return init;
  });
  const [counts, setCounts] = useState(() => {
    const init = {};
    selectedEngineers?.forEach(e => { if (e?.masterId) init[e.masterId] = e?.count ?? 1; });
    return init;
  });

  // Sync local state when selectedEngineers is replaced (e.g. loading a saved estimate)
  const prevIdsRef = useRef(null);
  useEffect(() => {
    const currentIds = (selectedEngineers || [])?.map(e => e?.masterId)?.sort()?.join(',');
    // Include count and duration in signature so edits to QTY on the same set of engineers are detected
    const currentSig = (selectedEngineers || [])
      ?.map(e => `${e?.masterId}:${e?.count ?? 1}:${e?.duration ?? 1}:${e?.timeUnit ?? 'hours'}`)
      ?.sort()
      ?.join('|');
    if (prevIdsRef?.current !== null && prevIdsRef?.current !== currentSig) {
      const newDurations = {};
      const newTimeUnits = {};
      const newCounts = {};
      (selectedEngineers || [])?.forEach(e => {
        if (e?.masterId) {
          newDurations[e.masterId] = e?.duration ?? 1;
          newTimeUnits[e.masterId] = e?.timeUnit ?? 'hours';
          newCounts[e.masterId] = e?.count ?? 1;
        }
      });
      setDurations(newDurations);
      setTimeUnits(newTimeUnits);
      setCounts(newCounts);
    }
    prevIdsRef.current = currentSig;
  }, [selectedEngineers]);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchEngineers = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getEngineers();
        setMasterEngineers(data || []);
      } catch (err) {
        console.error('Failed to load engineers:', err);
        setError('Failed to load engineers from master data.');
      } finally {
        setLoading(false);
      }
    };
    fetchEngineers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEngineers = masterEngineers?.filter(e =>
    e?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    e?.role?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    e?.specialization?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const isSelected = (id) => selectedEngineers?.some(e => e?.masterId === id);

  const getHours = (duration, timeUnit) => {
    const unit = TIME_UNITS?.find(u => u?.value === timeUnit);
    return parseFloat(duration || 1) * (unit?.toHours || 1);
  };

  // Calculate cost per hour based on the selected Unit (timeUnit)
  // Days: dailySalary / 9 hours per day
  // Months: monthlySalary / 240 hours per month
  // Hours: use hourlySalary directly
  const getCostPerHour = (engineer, timeUnit) => {
    const unit = timeUnit || 'hours';
    if (unit === 'days') {
      if (engineer?.dailySalary > 0) return parseFloat((engineer?.dailySalary / 9)?.toFixed(4));
      if (engineer?.monthlySalary > 0) return parseFloat(((engineer?.monthlySalary / 30) / 9)?.toFixed(4));
      if (engineer?.internalCost > 0) return parseFloat((engineer?.internalCost / 9)?.toFixed(4));
      return 0;
    }
    if (unit === 'months') {
      if (engineer?.monthlySalary > 0) return parseFloat((engineer?.monthlySalary / 240)?.toFixed(4));
      if (engineer?.internalCost > 0) return parseFloat((engineer?.internalCost / 240)?.toFixed(4));
      return 0;
    }
    // hours (default)
    if (engineer?.hourlySalary > 0) return parseFloat(engineer?.hourlySalary?.toFixed(4));
    if (engineer?.internalCost > 0) return parseFloat((engineer?.internalCost / 8)?.toFixed(4));
    if (engineer?.monthlySalary > 0) return parseFloat((engineer?.monthlySalary / 240)?.toFixed(4));
    return 0;
  };

  const getBillableRatePerHour = (engineer, timeUnit) => {
    if (engineer?.billableRate > 0) return parseFloat((engineer?.billableRate / 9)?.toFixed(4));
    return getCostPerHour(engineer, timeUnit);
  };

  // Prorate a monthly fixed cost (SPF or Health Insurance) to match the selected time unit.
  // Monthly → full value; Daily → value / 30; Hours → value / 30 / 9
  const prorateMonthlyAmount = (monthlyAmount, timeUnit) => {
    if (!monthlyAmount) return 0;
    if (timeUnit === 'days') return parseFloat(monthlyAmount) / 30;
    if (timeUnit === 'hours') return parseFloat(monthlyAmount) / 30 / 9;
    return parseFloat(monthlyAmount); // months
  };

  const handleToggle = (engineer) => {
    if (isSelected(engineer?.id)) {
      onEngineersChange(selectedEngineers?.filter(e => e?.masterId !== engineer?.id));
    } else {
      const duration = parseFloat(durations?.[engineer?.id]) || 1;
      const timeUnit = timeUnits?.[engineer?.id] || 'hours';
      const count = parseInt(counts?.[engineer?.id]) || 1;
      const hours = getHours(duration, timeUnit);
      const entry = {
        id: Date.now() + Math.random(),
        masterId: engineer?.id,
        role: engineer?.role,
        roleLabel: engineer?.name,
        specialization: engineer?.specialization || '',
        hours,
        duration,
        timeUnit,
        count,
        costPerHour: getCostPerHour(engineer, timeUnit),
        billableRate: getBillableRatePerHour(engineer, timeUnit),
        nationality: engineer?.nationality || 'expat',
        spfPercentage: engineer?.spfPercentage ?? 22.5,
        spfValue: engineer?.spfValue ?? 0,
        healthInsuranceEnabled: engineer?.healthInsuranceEnabled ?? false,
        healthInsuranceValue: engineer?.healthInsuranceValue ?? 0,
        monthlySalary: engineer?.monthlySalary ?? 0,
      };
      onEngineersChange([...selectedEngineers, entry]);
    }
  };

  const updateSelected = (engineerId, patch) => {
    const idx = selectedEngineers?.findIndex(e => e?.masterId === engineerId);
    if (idx >= 0) {
      const updated = [...selectedEngineers];
      updated[idx] = { ...updated?.[idx], ...patch };
      onEngineersChange(updated);
    }
  };

  const handleDurationChange = (engineerId, val, unit) => {
    const tu = unit || timeUnits?.[engineerId] || 'hours';
    setDurations(prev => ({ ...prev, [engineerId]: val }));
    const hours = getHours(val, tu);
    updateSelected(engineerId, { duration: parseFloat(val) || 1, hours, timeUnit: tu });
  };

  const handleTimeUnitChange = (engineerId, val) => {
    setTimeUnits(prev => ({ ...prev, [engineerId]: val }));
    const dur = durations?.[engineerId] || selectedEngineers?.find(e => e?.masterId === engineerId)?.duration || 1;
    const hours = getHours(dur, val);
    // Recalculate costPerHour based on new unit
    const masterEng = masterEngineers?.find(e => e?.id === engineerId);
    const newCostPerHour = masterEng ? getCostPerHour(masterEng, val) : undefined;
    const newBillableRate = masterEng ? getBillableRatePerHour(masterEng, val) : undefined;
    const patch = { timeUnit: val, hours };
    if (newCostPerHour !== undefined) patch.costPerHour = newCostPerHour;
    if (newBillableRate !== undefined) patch.billableRate = newBillableRate;
    updateSelected(engineerId, patch);
  };

  const handleCountChange = (engineerId, val) => {
    setCounts(prev => ({ ...prev, [engineerId]: val }));
    updateSelected(engineerId, { count: parseInt(val) || 1 });
  };

  const handleRemoveEngineer = (id) => {
    onEngineersChange(selectedEngineers?.filter(e => e?.id !== id));
  };

  const formatDuration = (engineer) => {
    if (engineer?.timeUnit && engineer?.duration) {
      const unitLabel = TIME_UNITS?.find(u => u?.value === engineer?.timeUnit)?.label || 'Hours';
      return `${engineer?.duration} ${unitLabel}`;
    }
    return `${engineer?.hours}h`;
  };

  const selectedCount = selectedEngineers?.length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-foreground">
          Required Engineers <span className="text-error">*</span>
        </label>
        {error && (
          <span className="text-xs text-error flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {error}
          </span>
        )}
      </div>
      {/* Searchable Dropdown Trigger */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen(prev => !prev)}
          disabled={loading}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all disabled:opacity-60"
        >
          <span className={selectedCount === 0 ? 'text-muted-foreground' : ''}>
            {loading ? 'Loading engineers...' : selectedCount === 0 ? 'Select engineers...' : `${selectedCount} engineer${selectedCount > 1 ? 's' : ''} selected`}
          </span>
          {loading
            ? <Icon name="Loader2" size={16} className="text-muted-foreground animate-spin" />
            : <Icon name={dropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
          }
        </button>

        {dropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search engineers..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground"
                  autoFocus
                />
              </div>
            </div>

            {/* Items List */}
            <div className="max-h-52 overflow-y-auto">
              {filteredEngineers?.length === 0 && (
                <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                  {masterEngineers?.length === 0 ? 'No engineers in master data. Add them in Master Data Management.' : 'No engineers found'}
                </div>
              )}
              {filteredEngineers?.map(engineer => {
                const checked = isSelected(engineer?.id);
                const selEntry = selectedEngineers?.find(e => e?.masterId === engineer?.id);
                const currentUnit = timeUnits?.[engineer?.id] ?? selEntry?.timeUnit ?? 'hours';
                return (
                  <div key={engineer?.id} className="px-3 py-2 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`eng-cb-${engineer?.id}`}
                        checked={checked}
                        onChange={() => handleToggle(engineer)}
                        className="w-4 h-4 rounded border-border text-primary accent-primary cursor-pointer"
                      />
                      <label htmlFor={`eng-cb-${engineer?.id}`} className="flex-1 cursor-pointer">
                        <div className="text-sm font-medium text-foreground">{engineer?.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {ROLE_LABELS?.[engineer?.role] || engineer?.role}
                          {engineer?.specialization ? ` · ${engineer?.specialization}` : ''}
                          {' · OMR '}
                          {getCostPerHour(engineer, currentUnit)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}/hr
                        </div>
                      </label>
                    </div>
                    {checked && (
                      <div className="mt-2 ml-6 space-y-1.5">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-0.5 block">Duration</label>
                            <input
                              type="number"
                              min="1"
                              value={durations?.[engineer?.id] ?? selEntry?.duration ?? 1}
                              onChange={e => handleDurationChange(engineer?.id, e?.target?.value)}
                              className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-0.5 block">Unit</label>
                            <select
                              value={currentUnit}
                              onChange={e => handleTimeUnitChange(engineer?.id, e?.target?.value)}
                              className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                            >
                              {TIME_UNITS?.map(u => <option key={u?.value} value={u?.value}>{u?.label}</option>)}
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-0.5 block">Count</label>
                            <input
                              type="number"
                              min="1"
                              value={counts?.[engineer?.id] ?? selEntry?.count ?? 1}
                              onChange={e => handleCountChange(engineer?.id, e?.target?.value)}
                              className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                            />
                          </div>
                        </div>
                        {currentUnit !== 'hours' && (
                          <p className="text-xs text-muted-foreground">
                            = {getHours(durations?.[engineer?.id] ?? selEntry?.duration ?? 1, currentUnit)?.toLocaleString()} hours
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Info footer */}
            <div className="border-t border-border px-3 py-2 bg-muted/20">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon name="Info" size={11} />
                Manage engineers in <span className="font-medium text-primary">Master Data Management</span>
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Selected Items List */}
      {selectedEngineers?.length > 0 ? (
        <div className="space-y-2">
          {selectedEngineers?.map((engineer) => {
            const baseCost = (engineer?.count || 1) * (engineer?.hours || 0) * (engineer?.costPerHour || 0);
            const timeUnit = engineer?.timeUnit || 'hours';
            const rawSpf = engineer?.nationality === 'omani' ? (engineer?.spfValue ?? 0) : 0;
            const rawHI = engineer?.healthInsuranceEnabled ? (engineer?.healthInsuranceValue ?? 0) : 0;
            const spfAmount = rawSpf * (engineer?.duration || 1) * (engineer?.count || 1);
            const hiAmount = rawHI * (engineer?.duration || 1) * (engineer?.count || 1);
            const totalCost = baseCost + spfAmount + hiAmount;
            return (
              <div
                key={engineer?.id}
                className="flex items-start justify-between gap-2 p-3 bg-card border border-border rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{engineer?.roleLabel}</div>
                  <div className="text-xs text-muted-foreground mt-1 break-words">
                    {engineer?.count} × {formatDuration(engineer)} @ OMR {engineer?.costPerHour?.toFixed ? engineer?.costPerHour?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : engineer?.costPerHour}/hr
                    <span className="hidden sm:inline"> (Bill: OMR {engineer?.billableRate?.toFixed ? engineer?.billableRate?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : engineer?.billableRate}/hr)</span>
                  </div>
                  {(spfAmount > 0 || hiAmount > 0) && (
                    <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
                      {spfAmount > 0 && <span>SPF: OMR {spfAmount?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>}
                      {hiAmount > 0 && <span>Health Ins: OMR {hiAmount?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-sm font-semibold text-foreground whitespace-nowrap">
                    OMR {totalCost?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                  </div>
                  <button
                    onClick={() => handleRemoveEngineer(engineer?.id)}
                    className="text-error hover:text-error/80 transition-smooth"
                    aria-label="Remove engineer"
                  >
                    <Icon name="Trash2" size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground text-sm">
          No engineers selected yet. Use the dropdown above to select engineers.
        </div>
      )}
    </div>
  );
};

export default EngineerSelector;